// ABOUTME: Checkbox list for selecting which family calendar sources to display for a child.
// ABOUTME: Groups calendars by provider connection and allows multi-select across providers.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { CalendarConnectionDto, CalendarSourceDto } from '../../types/api';
import { calendarAPI } from '../../services/api';

interface ChildCalendarCheckboxesProps {
  childId: number;
  connections: CalendarConnectionDto[];
}

const ChildCalendarCheckboxes: React.FC<ChildCalendarCheckboxesProps> = ({ childId, connections }) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSelections = useCallback(async () => {
    try {
      setLoading(true);
      const result = await calendarAPI.getChildSelections(childId);
      setSelectedIds(new Set(result.selectedCalendars.map(c => c.id)));
    } catch {
      // If no selections yet, start with empty set
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadSelections();
  }, [loadSelections]);

  const handleToggle = async (source: CalendarSourceDto) => {
    const newIds = new Set(selectedIds);
    if (newIds.has(source.id)) {
      newIds.delete(source.id);
    } else {
      newIds.add(source.id);
    }
    setSelectedIds(newIds);

    setSaving(true);
    try {
      await calendarAPI.updateChildSelections(childId, Array.from(newIds));
    } catch {
      // Revert on error
      loadSelections();
    } finally {
      setSaving(false);
    }
  };

  // Flatten all sources from all connections
  const hasAnySources = connections.some(c => c.calendarSources.length > 0);

  if (!hasAnySources) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          No calendars available. Add a calendar source and refresh to see available calendars.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box data-testid={`child-calendar-checkboxes-${childId}`} sx={{ px: 2, pb: 1 }}>
      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 600 }}>
        {/* Icon glyph sizing via fontSize, not a Typography text override */}
        <CalendarMonthIcon sx={{ fontSize: 14 }} /> Calendars
        {saving && <CircularProgress size={12} sx={{ ml: 1 }} />}
      </Typography>
      {connections.map(connection => (
        connection.calendarSources.length > 0 && (
          <Box key={connection.id}>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {connection.displayName}
            </Typography>
            <FormGroup sx={{ ml: 1 }}>
              {connection.calendarSources.map(source => (
                <FormControlLabel
                  key={source.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(source.id)}
                      onChange={() => handleToggle(source)}
                      disabled={saving}
                    />
                  }
                  label={
                    <Typography variant="body2">{source.displayName}</Typography>
                  }
                  sx={{ my: -0.5 }}
                />
              ))}
            </FormGroup>
          </Box>
        )
      ))}
    </Box>
  );
};

export default ChildCalendarCheckboxes;
